import React from 'react'
import { Nav, NavLink, NavbarContainer, Span, NavLogo, NavItems, GitHubButton, ButtonContainer, MobileMenu, MobileLink, IconButton, LanguageDropdown, LanguageMenu, LanguageOption } from './NavbarStyledComponent'
import { DiCssdeck } from 'react-icons/di';
import { FaBars, FaChevronDown } from 'react-icons/fa';
import { FaGlobe, FaMoon, FaSun } from 'react-icons/fa';
import { Bio } from '../../data/constants';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { supportedLanguages, defaultLanguage } from '../../i18n/languages';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { announceToScreenReader } from '../../utils/accessibility';

import { Link } from 'react-router-dom';

const MoreDropdown = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const MoreTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.text_primary};
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;
  padding: 8px 4px;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

const MoreMenu = styled(LanguageMenu)``;

const MoreMenuLink = styled.a`
  padding: 8px 12px;
  border-radius: 6px;
  color: ${({ theme }) => theme.text_primary};
  text-decoration: none;
  font-size: 14px;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.primary + '20'};
    color: ${({ theme }) => theme.primary};
  }
`;

const MemoryLink = styled(Link)`
  padding: 8px 12px;
  border-radius: 6px;
  color: ${({ theme }) => theme.text_primary};
  text-decoration: none;
  font-size: 14px;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.primary + '20'};
    color: ${({ theme }) => theme.primary};
  }
`;

const MenuToggleButton = styled.button`
  display: none;
  border: none;
  background: transparent;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${({ theme }) => theme.text_primary};
  padding: 8px;

  @media screen and (max-width: 768px) {
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(-100%, 60%);
  }
`;

const moreLinks = [
  { href: '#blog', labelKey: 'nav.blog' },
  { href: '#faq', labelKey: 'nav.faq' },
  { href: '#learning', labelKey: 'nav.learning' },
  { href: '#social-proof', labelKey: 'nav.socialProof' },
];

const Navbar = ({ darkMode, toggleTheme }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = React.useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = React.useState(false);
  const theme = useTheme();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { lng } = useParams();
  const homePath = `/${lng || i18n.language || defaultLanguage}`;

  const changeLanguage = (nextLng) => {
    const safeLng = supportedLanguages.some((l) => l.code === nextLng) ? nextLng : defaultLanguage;
    i18n.changeLanguage(safeLng);
    localStorage.setItem('language', safeLng);
    const [, , ...rest] = location.pathname.split('/');
    const nextPath = `/${safeLng}/${rest.join('/')}`.replace(/\/+$/, '') || `/${safeLng}`;
    navigate(`${nextPath}${location.hash}`, { replace: true });
    setLanguageMenuOpen(false);
    const langLabel = supportedLanguages.find((l) => l.code === safeLng)?.label || safeLng;
    announceToScreenReader(`Language changed to ${langLabel}`);
  };

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageMenuOpen && !event.target.closest('.language-dropdown')) {
        setLanguageMenuOpen(false);
      }
      if (moreMenuOpen && !event.target.closest('.more-dropdown')) {
        setMoreMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [languageMenuOpen, moreMenuOpen]);

  const closeMobile = () => setIsOpen(false);

  return (
    <Nav as="header">
      <NavbarContainer>
        <NavLogo to={homePath} aria-label="Portfolio home">
          <DiCssdeck size="3rem" aria-hidden="true" />
          <Span>My Portfolio</Span>
        </NavLogo>
        <MenuToggleButton
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
          aria-expanded={isOpen}
        >
          <FaBars />
        </MenuToggleButton>
        <NavItems>
          <NavLink href="#about">{t('nav.about')}</NavLink>
          <NavLink href='#skills'>{t('nav.skills')}</NavLink>
          <NavLink href='#experience'>{t('nav.experience')}</NavLink>
          <NavLink href='#projects'>{t('nav.projects')}</NavLink>
          <NavLink href='#education'>{t('nav.education')}</NavLink>
          <MoreDropdown className="more-dropdown">
            <MoreTrigger
              type="button"
              onClick={() => setMoreMenuOpen(!moreMenuOpen)}
              aria-expanded={moreMenuOpen}
              aria-haspopup="true"
            >
              {t('nav.more')}
              <FaChevronDown size={12} />
            </MoreTrigger>
            <MoreMenu isOpen={moreMenuOpen}>
              {moreLinks.map((link) => (
                <MoreMenuLink key={link.href} href={link.href}>
                  {t(link.labelKey)}
                </MoreMenuLink>
              ))}
              <MemoryLink to="/memory-wiki">Memory Wiki</MemoryLink>
            </MoreMenu>
          </MoreDropdown>
          <NavLink href='#contact'>{t('nav.contact')}</NavLink>
        </NavItems>
        <ButtonContainer>
          <LanguageDropdown className="language-dropdown">
            <IconButton
              type="button"
              onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
              aria-label="Switch language"
              aria-expanded={languageMenuOpen}
            >
              <FaGlobe />
            </IconButton>
            <LanguageMenu isOpen={languageMenuOpen}>
              {supportedLanguages.map((lang) => (
                <LanguageOption
                  key={lang.code}
                  type="button"
                  active={i18n.language === lang.code}
                  onClick={() => changeLanguage(lang.code)}
                >
                  {lang.label}
                </LanguageOption>
              ))}
            </LanguageMenu>
          </LanguageDropdown>
          <IconButton
            type="button"
            onClick={toggleTheme}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleTheme();
              }
            }}
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? <FaSun /> : <FaMoon />}
          </IconButton>
          <GitHubButton href={Bio.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub Profile">{t('nav.githubProfile')}</GitHubButton>
        </ButtonContainer>
        {
          isOpen &&
          <MobileMenu isOpen={isOpen}>
            <MobileLink href="#about" onClick={closeMobile}>{t('nav.about')}</MobileLink>
            <MobileLink href='#skills' onClick={closeMobile}>{t('nav.skills')}</MobileLink>
            <MobileLink href='#experience' onClick={closeMobile}>{t('nav.experience')}</MobileLink>
            <MobileLink href='#projects' onClick={closeMobile}>{t('nav.projects')}</MobileLink>
            <MobileLink href='#education' onClick={closeMobile}>{t('nav.education')}</MobileLink>
            {moreLinks.map((link) => (
              <MobileLink key={link.href} href={link.href} onClick={closeMobile}>
                {t(link.labelKey)}
              </MobileLink>
            ))}
            <MobileLink href='#contact' onClick={closeMobile}>{t('nav.contact')}</MobileLink>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center', marginTop: '8px', position: 'relative' }}>
              <LanguageDropdown className="language-dropdown" style={{ position: 'relative' }}>
                <IconButton
                  type="button"
                  onClick={() => {
                    setLanguageMenuOpen(!languageMenuOpen);
                  }}
                  aria-label="Switch language"
                  style={{ width: '40px', height: '40px' }}
                >
                  <FaGlobe />
                </IconButton>
                <LanguageMenu isOpen={languageMenuOpen} style={{ position: 'absolute', top: 'calc(100% + 8px)', right: '0' }}>
                  {supportedLanguages.map((lang) => (
                    <LanguageOption
                      key={lang.code}
                      type="button"
                      active={i18n.language === lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        closeMobile();
                      }}
                    >
                      {lang.label}
                    </LanguageOption>
                  ))}
                </LanguageMenu>
              </LanguageDropdown>
              <IconButton
                type="button"
                onClick={toggleTheme}
                aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                style={{ width: '40px', height: '40px' }}
              >
                {darkMode ? <FaSun /> : <FaMoon />}
              </IconButton>
            </div>
            <GitHubButton 
              style={{padding: '10px 16px',background: `${theme.primary}`, color: 'white',width: 'max-content'}} 
              href={Bio.github} 
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub Profile"
            >
              {t('nav.githubProfile')}
            </GitHubButton>
          </MobileMenu>
        }
      </NavbarContainer>
    </Nav>
  )
}

export default Navbar
